import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { Chapter, Prose, TheoryBox, MTQuote } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";

export default function Beatles5() {
  const { lesson, course, accent } = findLesson("the-beatles", "5");
  return (
    <LessonShell
      course={course}
      courseSlug="the-beatles"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <Chapter eyebrow="The starting point" title="Get the scale — then get the available chords">
        <Prose>
          <p>
            "Because" lives in C♯ minor. Jason's first instruction when approaching any song
            is exactly this: start with the scale, then identify every chord available from it.
            All of the chords so far in "Because" are hailing from the C♯ minor scale. Once you
            know the scale, you have a ballpark — a map. You won't be lost in the jungle doing
            trial and error with too many permutations to survive.
          </p>
          <p>
            The instrument carrying the opening is somewhere between a harpsichord and a
            clavichord — that very ancient, baroque quality. Jason notes that both the guitar and
            this keyboard instrument play together, slowly building up. When the guitar comes in,
            the chord is A major. Still C♯ minor. Still the map.
          </p>
          <p>
            And then the piece begins its chord journey. Jason counted: <strong>eight chords
            in the first 23 seconds</strong>. Normal chords, as he puts it — just played very
            beautifully with these arpeggios.
          </p>
        </Prose>

        <TheoryBox label="Quick stats">
          <strong>Key:</strong> C♯ minor (harmonic minor) ·{" "}
          <strong>Meter:</strong> 4/4 ·{" "}
          <strong>Album:</strong> Abbey Road (1969) ·{" "}
          <strong>First 23 seconds:</strong> 8 chords — all from the C♯ minor world, until they aren't
        </TheoryBox>
      </Chapter>

      <YouTubeEmbed
        videoId="hL0tnrl2L_U"
        title="Because — The Beatles"
        caption="Listen to the first 23 seconds. Count the chord changes — Jason tracked eight. They are all C♯ minor scale chords until the A7 arrives. That A7 is the moment everything changes."
      />

      <Chapter eyebrow="Half diminished vs full diminished" title="Two kinds of tension — one note apart">
        <Prose>
          <p>
            Before getting into "Because" chord by chord, Jason draws a crucial distinction in
            the harmonic minor world: the difference between a <strong>half diminished</strong> and
            a <strong>fully diminished</strong> chord. Both exist. Both have their uses.
          </p>
          <p>
            Half diminished: 1, ♭3, ♭5, ♭7. Fully diminished: 1, ♭3, ♭5, <em>double flat 7</em>.
            That double flat 7 is one semitone lower than the already-flatted 7. One note, enormous
            difference in feel. The half diminished has a slight openness to it — like a tense question.
            The fully diminished is sealed shut, tightly coiled, all four intervals equal.
          </p>
          <p>
            In C♯ minor, the second scale degree is D♯. The chord built on D♯ is the one Jason
            is examining here: D♯ half diminished (also written D♯m7♭5) or D♯ fully diminished.
            In "Because," the half-diminished D♯m7♭5 is the song's second chord; a fully
            diminished chord arrives later, built on D (Ddim7).
          </p>
        </Prose>

        <TheoryBox label="Diminished comparison">
          Half diminished (m7♭5): 1, ♭3, ♭5, ♭7 — e.g., D♯ F♯ A C♯. ·{" "}
          Fully diminished (dim7): 1, ♭3, ♭5, ♭♭7 — e.g., D♯ F♯ A C. ·{" "}
          The double-flat 7 drops one more semitone. Both chords exist in harmonic minor.
          The fully diminished divides the octave into four equal minor thirds — it has no
          natural direction, no pull toward any particular resolution.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="The 2-5-1 in minor" title="Same function, different colour">
        <Prose>
          <p>
            The 2-5-1 cadence is the engine of tonal harmony in major. It also exists in minor —
            but with a different flavour. Jason explains the minor version: "A 2-5-1 in minor
            would end up being 2, 5 usually with a flat 9, and a 1 minor." The flat 9 on the
            dominant is what gives minor cadences their particular intensity. It's a very common
            chord progression used in harmonic minor songs.
          </p>
          <p>
            In C♯ minor this reads: D♯m7♭5 (iiø7) — G♯7♭9 (V7♭9) — C♯m (i). That G♯7♭9 is
            the moment of maximum pull: the dominant with an added tension (♭9) pressing hard
            against the tonic. The resolution to C♯m is a release of that pressure.
          </p>
          <p>
            "Because" moves through this kind of cadential territory. The song's arpeggio
            pattern — beautifully simple once you see it — rolls through these chords without
            telegraphing the harmony. The chords are not announced. They are played.
          </p>
        </Prose>

        <RomanNumeralAnalysis
          label="Because — C♯ minor chord map (first section)"
          caption="Jason's chord map, in order: C♯m → D♯m7♭5 → G♯ (his chart writes it A♭, the same keys) → A → C♯m, all from C♯ harmonic minor. Then A7 and A13 (no 5) step outside the scale with a G natural and lead to D. 'Normal chords actually just played very beautifully with these arpeggios.' — Jason"
          chords={[
            { chord: "C#m", rn: "i", function: "Tonic" },
            { chord: "D#m7b5", rn: "iiø7", function: "Pre-dominant" },
            { chord: "G#", rn: "V", function: "Dominant" },
            { chord: "A", rn: "♭VI", function: "Pre-dominant" },
            { chord: "C#m", rn: "i", function: "Tonic" },
            { chord: "A7", rn: "V7/♭II", function: "Secondary V" },
            { chord: "A13(no5)", rn: "V13/♭II", function: "Secondary V" },
          ]}
        />
      </Chapter>

      <Chapter eyebrow="The A7 — and what the 13th means" title="Non-diatonic, and a huge jump">
        <Prose>
          <p>
            After the C♯ minor world is established, an A7 chord arrives. Jason flags it
            immediately: "A seventh, which is now non-diatonic. It's a huge jump." The A chord
            exists in C♯ minor — but A7 (with the added G natural) does not. G natural is not
            in the C♯ harmonic minor scale. A7 is already out of the context.
          </p>
          <p>
            Then it gets further extended. The harmony moves to A9, then to <strong>A13</strong>.
            What is a 13th chord? Jason explains: "Whenever you have a 7 and when you consider
            something as a 13, the 13 is nothing but a 6. So I'm just writing a plus 6 there for
            your information." The extended intervals — 9, 11, 13 — are carbon copies of 2, 4,
            and 6, just played alongside the 7. A7-13 means the chord has the seventh and the
            sixth (13th) stacked together.
          </p>
          <p>
            The voicing Jason hears in the song is built from A, G and C♯, with G and C♯ doubled
            higher up and a very high F♯ — the 13th — on top. He writes it as A7 13 no 5, because
            the fifth (E) is omitted in this particular voicing. The 5 is often the first note dropped when you're stacking
            upper extensions — it contributes the least character.
          </p>
        </Prose>

        <TheoryBox label="Extended chord naming: 9, 11, 13">
          9 = the 2nd interval played alongside the 7th. ·{" "}
          11 = the 4th interval played alongside the 7th. ·{" "}
          13 = the 6th interval played alongside the 7th. ·{" "}
          So A13 = A major chord + the 7th + the 13th (6th). If the 5th is not
          voiced, write: A7-13 no 5. The 5th is typically the first note to omit when
          stacking extensions — it adds the least harmonic character.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="The vocal harmony" title="Chord tones, then diatonic, then chromatic">
        <Prose>
          <p>
            "Because" has three-part vocal harmony throughout the entire song. Jason explains
            the hierarchy that governs how vocal harmony is composed — and this hierarchy applies
            to any harmony, not just "Because":
          </p>
          <p>
            First priority: <strong>chord tones</strong>. The notes of the chord itself — root,
            third, fifth, seventh. These are the safest, most resonant options for a harmony
            voice. A chord tone will always fit because it is literally part of the harmony being
            played.
          </p>
          <p>
            Second option: the <strong>diatonic world</strong>. The seven-note scale world. If you
            want more of an adventure, you borrow scale notes that aren't in the chord but are still
            in the key. These are passing notes — they move between chord tones without sounding wrong.
          </p>
          <p>
            Third option: <strong>chromatic</strong>. Notes outside the scale entirely. This is the
            rarest option — used for maximum colour, maximum tension. "If you want to have an even
            more adventure, then you expand chromatically."
          </p>
          <p>
            The Beatles follow this model. "Because" sits mostly in the chord tone and diatonic
            layers — the harmony is carefully chosen to be consonant with the underlying chords —
            with the occasional borrowed note from the scale.
          </p>
        </Prose>

        <NoteImage
          src="/notes/the-beatles/finals/13-because-pt-1.png"
          caption="Jason's handwritten chord map of 'Because' — Part 1. The eight chords of the opening are marked, with the A7 → A9 → A13 extension sequence shown. Note how far out of the C♯ minor context the A7 sits."
        />

        <MTQuote>
          "Normal chords actually just played very beautifully with these arpeggios." — Jason,
          on why 'Because' is more accessible than it sounds. The chords are not exotic. The
          presentation is.
        </MTQuote>
      </Chapter>
    </LessonShell>
  );
}
